import React from 'react';
import { Node as ReteNode } from 'rete';
import { useFormContext } from 'react-hook-form';

import Node from 'types/node';

import Input from 'pipelines/services/rete/controls/input';
import { objectHas } from 'services/object';
import { serializeMultilineROI } from 'pipelines/services/rete/controls/multisegment_line_input';
import { serializeROI } from 'pipelines/services/rete/controls/roi_input';
import { VideoSourceROIValue } from 'pipelines/components/VideoSourceROI/VideoSourceROI';
import { VideoSourceSelection } from 'pipelines/services/rete/controls/video_source_selector';

import { useDeploymentForm } from '../context/deployment_form_context';

export type DeploymentEditableControlProps = {
  onChange: (event: any, errorMessage?: string) => void;
  id: string;
  control: Input;
  node: Node;
  disabled: boolean;
  reteNode: ReteNode;
  property: string;
  value: any;
  keyrefValue: any;
  resetSignal: number;
};

export function DeploymentEditableControl({
  id,
  control,
  property,
  node,
  disabled,
  value,
  keyrefValue,
  onChange,
  resetSignal,
}: DeploymentEditableControlProps) {
  const { setSources } = useDeploymentForm();
  const { clearErrors, setError, setValue } = useFormContext();

  const [internalValue, setInternalValue] = React.useState<
    VideoSourceROIValue | VideoSourceSelection | string | undefined
  >(() => {
    if (
      control.type === 'line' ||
      control.type === 'multiline' ||
      control.type === 'polygon' ||
      property === 'source_id'
    ) {
      return undefined;
    }

    return value;
  });

  React.useEffect(() => {
    setInternalValue(value ?? node.data[property]);
  }, [value, node.data, property]);

  React.useEffect(() => {
    if (property === 'source_id') {
      if (value && keyrefValue) {
        setSources((sources) => ({
          ...sources,
          [node.id!]: {
            source_id: value,
            source_type: keyrefValue,
          },
        }));
        setInternalValue({
          source_id: value,
          source_type: keyrefValue,
        });
        return;
      }

      setSources(({ [node.id!]: removedSource, ...sources }) => sources);
      setInternalValue(undefined);
    }

    if (control.type === 'line' || control.type === 'polygon') {
      const labels = control.keyref ? keyrefValue : undefined;

      setInternalValue({
        labels,
        coordinates: value,
      });
      return;
    }

    if (control.type === 'multiline') {
      let labels = control.keyref ? keyrefValue : undefined;
      let coordinates = value;

      if (Array.isArray(labels)) {
        labels = labels.join(';');
      }
      if (Array.isArray(coordinates)) {
        coordinates = coordinates.join(';');
      }

      setInternalValue({
        labels,
        coordinates,
      });
      return;
    }

    setInternalValue(value);
  }, [
    property,
    setSources,
    node.id,
    control.type,
    control.keyref,
    value,
    keyrefValue,
  ]);

  const handleChange = React.useCallback(
    (value: any, errorMessage?: string) => {
      const controlName = `${node.id}.${property}`;

      if (errorMessage) {
        setError(controlName, { type: 'manual', message: errorMessage });
      } else {
        clearErrors(controlName);
      }

      if (
        property === 'source_id' &&
        (!value || objectHas(value, 'source_id'))
      ) {
        setValue(`${node.id}.source_id`, value?.source_id || null);
        setValue(`${node.id}.source_type`, value?.source_type || null);
        onChange(value?.source_id || null);
        return;
      }

      switch (control.type) {
        case 'line':
        case 'polygon': {
          const roiCoordinates = serializeROI(
            `${node.id}.${control.key}`,
            `${node.id}.${control.keyref}`,
            value,
            setValue
          );
          onChange(roiCoordinates);
          break;
        }
        case 'multiline': {
          const roiCoordinates = serializeMultilineROI(
            `${node.id}.${control.key}`,
            `${node.id}.${control.keyref}`,
            value,
            setValue
          );
          onChange(roiCoordinates);
          break;
        }
        default: {
          if (value === '') {
            onChange(null);
            break;
          }

          // json-type controls (e.g. RadioSelect with object values) emit
          // JSON strings, but the API expects parsed objects.
          if (control.type === 'json' && typeof value === 'string') {
            try {
              onChange(JSON.parse(value));
              break;
            } catch {
              // Not valid JSON, fall through
            }
          }

          onChange(value);
        }
      }
    },
    [
      clearErrors,
      onChange,
      setError,
      setValue,
      property,
      node.id,
      control.key,
      control.type,
      control.keyref,
    ]
  );

  const controlComponentKey = `${node.id}.${control.key}.${resetSignal}`;

  return (
    <control.component
      key={controlComponentKey}
      {...control.props}
      id={id}
      value={internalValue}
      disabled={disabled}
      onChange={handleChange}
      keyrefValue={keyrefValue}
    />
  );
}
